import { useMemo } from 'react';
import type { Euler } from 'three';

import {
  createEditableKeyPoseScene,
  getKeyPoseIkEffectorWorldPosition,
} from '../ik/keyPoseIkSystem';
import { useStore as useKeyPoseStore } from '../keyPoseStore';
import { useCurrentFrameKeyPoseEditing } from './useCurrentFrameKeyPoseEditing';

interface UseKeyPoseIkEditingInput {
  sceneRotation?: Euler;
}

export function useKeyPoseIkEditing({
  sceneRotation,
}: UseKeyPoseIkEditingInput) {
  const keyPoseStore = useKeyPoseStore();
  const context = useCurrentFrameKeyPoseEditing({ sceneRotation });
  const { actorScene, keyPose } = context;
  const { editing } = keyPoseStore.state;
  const effectorId =
    editing.mode === 'ik' ? editing.selectedIkEffectorId : null;
  const targetPositions = keyPose
    ? (keyPoseStore.state.editing.ikTargetPositionsByKeyPoseId[keyPose.id] ??
      {})
    : {};
  const handlePosition = useMemo(() => {
    if (!keyPose || !effectorId) {
      return null;
    }

    const authoredPosition = targetPositions[effectorId];
    if (authoredPosition) {
      return authoredPosition;
    }

    const scene = createEditableKeyPoseScene({
      actorScene,
      keyPose,
      sceneRotation,
    });
    return scene ? getKeyPoseIkEffectorWorldPosition(scene, effectorId) : null;
  }, [actorScene, effectorId, keyPose, sceneRotation, targetPositions]);

  return {
    effectorId,
    handlePosition,
    ...context,
    lockFeet: editing.lockFeet,
    targetPositions,
  };
}
